/**
 * Cognito 操作の失敗時に画面に出す文言。
 *
 * Cognito の例外メッセージは英語のため、そのまま出さず例外の種別 (`Error.name`) ごとに日本語へ差し替える。
 * 同じ種別でもフローによって意味が変わる (例: NotAuthorizedException はサインアップでは受付停止、サインインではパスワード誤り) ため、フローごとに定義する。
 */
export const SIGN_UP_ERROR_MESSAGES = {
  UsernameExistsException: "このメールアドレスはすでに登録されています",
  InvalidPasswordException:
    "パスワードが要件を満たしていません。13文字以上で、大文字・小文字・数字・記号をそれぞれ含めてください",
  InvalidParameterException:
    "入力内容に誤りがあります。確認してもう一度お試しください",
  CodeDeliveryFailureException:
    "確認コードを送信できませんでした。メールアドレスを確認してもう一度お試しください",
  LimitExceededException:
    "試行回数の上限に達しました。しばらく時間をおいてからもう一度お試しください",
  TooManyRequestsException:
    "リクエストが集中しています。しばらく時間をおいてからもう一度お試しください",
  NotAuthorizedException: "現在、新規アカウントの作成は受け付けていません",
} as const;

export const SIGN_UP_FALLBACK_ERROR_MESSAGES = {
  UNKNOWN_ERROR:
    "エラーが発生しました。入力内容を確認してもう一度お試しください",
  NON_ERROR_THROWN:
    "予期せぬエラーが発生しました。入力内容を確認してもう一度お試しください",
} as const;

// サインインの Cognito エラーは本文で判別する必要があるため、日本語への変換は SignInForm 側で行う
export const SIGN_IN_FALLBACK_ERROR_MESSAGES = {
  UNKNOWN_ERROR:
    "エラーが発生しました。再度メールアドレスとパスワードを入力してください",
  NON_ERROR_THROWN:
    "予期せぬエラーが発生しました。再度メールアドレスとパスワードを入力してください",
} as const;

export const RESET_PASSWORD_ERROR_MESSAGES = {
  CodeMismatchException:
    "認証コードが正しくありません。確認してもう一度入力してください",
  ExpiredCodeException:
    "認証コードの有効期限が切れています。もう一度認証コードを送信してください",
  InvalidPasswordException:
    "パスワードが要件を満たしていません。13文字以上で、大文字・小文字・数字・記号をそれぞれ含めてください",
  LimitExceededException:
    "試行回数の上限に達しました。しばらく時間をおいてからもう一度お試しください",
  TooManyRequestsException:
    "リクエストが集中しています。しばらく時間をおいてからもう一度お試しください",
} as const;

export const RESET_PASSWORD_FALLBACK_ERROR_MESSAGES = {
  UNKNOWN_ERROR:
    "エラーが発生しました。認証コードの有効期限が切れている可能性があります。",
  REQUEST_FAILED: "エラーが発生しました。再度メールアドレスを入力してください",
} as const;

const hasErrorMessage = <T extends Record<string, string>>(
  messages: T,
  name: string,
): name is Extract<keyof T, string> => Object.hasOwn(messages, name);

export const getCognitoErrorMessage = <T extends Record<string, string>>(
  messages: T,
  name: string,
  fallback: string,
): string => (hasErrorMessage(messages, name) ? messages[name] : fallback);
